import cx from 'classnames'
import { FC, useEffect, useState } from 'react'
import { formatAggressorLLPrice } from '../../../helpers/formatting'
import { Styles } from '../../../helpers/types'
import { OrderStatus, OrderType } from '../../../store/order/types'
import { getSpreadEligibility } from '../../../store/rfq/helpers'
import { useAppSelector } from '../../../store/types'
import { showTsySetting } from '../../../store/webSettings/selectors'
import { SecurityModalInfo } from '../../../store/windows/types'
import LeftInfoPanel from '../LeftInfoPanel'
import PriceHedge from '../PriceHedge'
import TradeAmountInput from '../TradeAmountInput'

interface Props {
  type: OrderType
  securityModalInfo: SecurityModalInfo
  tradeAmt: string | undefined
  // FIXME: define style classname types
  styles: Styles
  orderStatusComp: OrderStatus | undefined
  shouldAlertPriceChange?: boolean
  shouldAlertSizeChange?: boolean
  onChangeTradeAmt?: (newValue: string) => void
  spotCrossSelection: string
  setSpotCrossSelection: (st: string) => void
  selectedAccount?: string
  setSelectedAccount?: (st: string) => void
  currency: string
  buySellAmount: number
  isTreasury: boolean
  rating: string
}

const disablingStatuses: OrderStatus[] = [
  'accepted',
  'rejected',
  'waitingForConfirmation'
]

const AggressorModalContent: FC<Props> = (props) => {
  const {
    type,
    securityModalInfo,
    tradeAmt,
    styles,
    onChangeTradeAmt,
    spotCrossSelection,
    setSpotCrossSelection,
    orderStatusComp,
    shouldAlertPriceChange,
    shouldAlertSizeChange,
    selectedAccount,
    setSelectedAccount,
    currency,
    buySellAmount,
    isTreasury,
    rating
  } = props

  const showTsy = useAppSelector(showTsySetting)

  const [allInPrice, setAllInPrice] = useState(0)
  const [bmPrice, setBmPrice] = useState<number | undefined>(0)

  useEffect(() => {
    if (
      (orderStatusComp === 'creationPending' && !buySellAmount) ||
      orderStatusComp === 'cancelled'
    ) {
      setAllInPrice(securityModalInfo.allInPrice)
      setBmPrice(securityModalInfo.benchmarkPrice)
    }
  }, [
    orderStatusComp,
    securityModalInfo.allInPrice,
    securityModalInfo.benchmarkPrice
  ])

  const minSizeForTsyHedge = securityModalInfo.minSizeForTsyHedge
    ? securityModalInfo.minSizeForTsyHedge
    : '500'

  const statusDisable = disablingStatuses.includes(orderStatusComp!)

  const disableCrossButton =
    statusDisable ||
    !bmPrice ||
    Number(tradeAmt) < Number(minSizeForTsyHedge) ||
    !getSpreadEligibility(rating, securityModalInfo.spread)

  return (
    <div
      className={cx(
        styles.content,
        type === 'buy' ? styles.isBuy : styles.isSell,
        setSelectedAccount ? styles.aggressorContent : styles.lastLookContent
      )}
    >
      <LeftInfoPanel
        currency={currency}
        isTreasury={isTreasury}
        amountValueStyles={shouldAlertSizeChange ? styles.warning : ''}
        priceValueStyles={shouldAlertPriceChange ? styles.warning : ''}
        securityModalInfo={securityModalInfo}
        selectedAccount={selectedAccount}
        setSelectedAccount={setSelectedAccount}
        setMaxTradeAmt={onChangeTradeAmt}
        maxDisabled={statusDisable}
        tradeAmt={
          <TradeAmountInput
            value={tradeAmt}
            inputDisabled={statusDisable}
            inputClass={styles.buySellColor}
            minimumSize={securityModalInfo.minimumSize}
            onChange={onChangeTradeAmt}
          />
        }
        updateOnChanges={[
          securityModalInfo.price,
          securityModalInfo.allInPrice,
          securityModalInfo.yieldToWorst,
          securityModalInfo.benchmarkPrice,
          securityModalInfo.hedgeNotional
        ]}
      />
      <div className={styles.rightInfos}>
        <div className={styles.allInPrice}>
          <p>All-in Price</p>
          <p
            data-testid="aggressor-all-in-price"
            className={cx(
              styles.value,
              shouldAlertPriceChange && styles.warning
            )}
          >
            {formatAggressorLLPrice(allInPrice, currency, isTreasury)}
          </p>
          {showTsy && (
            <PriceHedge
              disableBtns={statusDisable}
              disableCrossBtn={disableCrossButton}
              setSpotCrossSelection={setSpotCrossSelection}
              spotCrossSelection={spotCrossSelection}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default AggressorModalContent
